<template>
  <div 
    v-if="message"
    :class="[
      'rounded-md p-4',
      variant === 'error' ? 'bg-red-50' : 'bg-yellow-50'
    ]"
  >
    <div class="flex">
      <div class="shrink-0">
        <svg
          v-if="variant === 'error'"
          class="h-5 w-5 text-red-400"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
        </svg>
        <svg
          v-else
          class="h-5 w-5 text-yellow-400"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>
      </div>
      <div class="ml-3">
        <h3 :class="[
          'text-sm font-medium mb-1',
          variant === 'error' ? 'text-red-800' : 'text-yellow-800'
        ]">
          {{ title || (variant === 'error' ? 'Er is een fout opgetreden' : 'Let op') }}
        </h3>
        <p :class="[
          'text-sm',
          variant === 'error' ? 'text-red-700' : 'text-yellow-700'
        ]">
          {{ message }}
        </p>
        
        <!-- Details/Validation Errors -->
        <div v-if="details && details.length" class="mt-2 text-sm" :class="variant === 'error' ? 'text-red-700' : 'text-yellow-700'">
          <ul class="list-disc list-inside space-y-1">
            <li v-for="(detail, index) in details" :key="index">
              {{ detail }}
            </li>
          </ul>
        </div>
        
        <!-- Suggestions -->
        <div v-if="suggestions && suggestions.length" class="mt-3">
          <p :class="[
            'text-sm font-medium mb-2',
            variant === 'error' ? 'text-red-800' : 'text-yellow-800'
          ]">
            Probeer het volgende:
          </p>
          <ul :class="[
            'text-sm space-y-1 list-disc list-inside',
            variant === 'error' ? 'text-red-700' : 'text-yellow-700'
          ]">
            <li v-for="suggestion in suggestions" :key="suggestion">{{ suggestion }}</li>
          </ul>
        </div>
        
        <!-- Action Buttons -->
        <div v-if="actions && actions.length" class="mt-4 flex space-x-3">
          <button
            v-for="action in actions"
            :key="action.label"
            @click="action.handler"
            :class="[
              'inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md focus:outline-hidden focus:ring-2 focus:ring-offset-2 transition-colors',
              action.primary
                ? (variant === 'error' 
                  ? 'text-white bg-red-600 hover:bg-red-700 focus:ring-red-500' 
                  : 'text-white bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500')
                : (variant === 'error'
                  ? 'text-red-700 bg-red-100 hover:bg-red-200 focus:ring-red-500'
                  : 'text-yellow-700 bg-yellow-100 hover:bg-yellow-200 focus:ring-yellow-500')
            ]"
          >
            {{ action.label }}
          </button>
        </div>
      </div>
      <div v-if="dismissible" class="ml-auto pl-3">
        <div class="-mx-1.5 -my-1.5">
          <button
            type="button"
            @click="$emit('dismiss')"
            :class="[
              'inline-flex rounded-md p-1.5 focus:outline-hidden focus:ring-2 focus:ring-offset-2',
              variant === 'error' 
                ? 'text-red-500 bg-red-50 hover:bg-red-100 focus:ring-red-600 focus:ring-offset-red-50' 
                : 'text-yellow-500 bg-yellow-50 hover:bg-yellow-100 focus:ring-yellow-600 focus:ring-offset-yellow-50'
            ]"
          >
            <span class="sr-only">Sluiten</span>
            <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
interface ErrorAction {
  label: string
  handler: () => void
  primary?: boolean
}

interface Props {
  message: string
  title?: string
  details?: string[]
  suggestions?: string[]
  actions?: ErrorAction[]
  variant?: 'error' | 'warning'
  dismissible?: boolean
}

withDefaults(defineProps<Props>(), {
  variant: 'error',
  dismissible: false
})

defineEmits<{
  dismiss: []
}>()
</script>